/**
 * Capa de fetch tipada para el flujo de aceptación de invitaciones de
 * PLATAFORMA (ADR 0012 — Fase 1).
 *
 * Endpoints bajo /api/auth/platform-invitation/*, requieren:
 *   - credentials: "include"
 *   - X-Requested-With: "portal-dashboard" (CSRF defense en POSTs)
 *
 * A diferencia de invitation-api.ts (tenant), este flujo tiene UNA sola rama
 * feliz (signup-required) — un user preexistente con el email es un error de
 * estado (409 USER_EXISTS), no un camino alternativo. Ver ADR 0012 §Race con
 * user preexistente.
 *
 * Módulo aislado de invitation-api.ts para no acoplar el plano plataforma al
 * plano tenant — los contratos son paralelos pero no idénticos.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:6787";

// ---------------------------------------------------------------------------
// Helpers de fetch
// ---------------------------------------------------------------------------

async function platformInvitationPost<T>(
  path: string,
  body: Record<string, string>,
): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "portal-dashboard",
    },
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Tipos de respuesta — shapes reales del worker
// (apps/worker-api/src/infrastructure/auth/platform-invitation-accept.ts)
// ---------------------------------------------------------------------------

/**
 * Modos posibles devueltos por POST /api/auth/platform-invitation/preview.
 *
 * - "signup-required" → invitación válida, sin cuenta existente: el invitado
 *   completa el formulario de signup.
 * - "invalid" → inexistente, expirada, cancelada, accepted, o user
 *   preexistente (todo colapsa a este modo genérico — anti-leak).
 */
export type PlatformInvitationPreviewMode = "signup-required" | "invalid";

export interface PlatformInvitationPreviewResponse {
  mode: PlatformInvitationPreviewMode;
  /** Email de destino de la invitación (solo si mode === "signup-required") */
  email?: string;
  /** Rol de plataforma que recibirá el invitado */
  role?: string;
  /** Nombre (o email) de quien envió la invitación */
  inviterName?: string | null;
  requiresPassword?: boolean;
}

export interface PlatformAcceptSignupResponse {
  ok: true;
  /** Ruta a la que navegar tras el signup exitoso */
  redirectTo: string;
  /** Presente solo si el auto sign-in post-creación falló (cuenta ya creada) */
  warning?: string;
}

// ---------------------------------------------------------------------------
// Errores del dominio
// ---------------------------------------------------------------------------

/**
 * Códigos de error que puede devolver el worker. El backend usa mensajes
 * genéricos anti-oráculo — `code` solo está presente para el caso
 * INVITATION_INVALIDATED (bloqueo por intentos) y USER_EXISTS.
 */
export type PlatformInvitationErrorCode = "INVITATION_INVALIDATED" | "USER_EXISTS";

export class PlatformInvitationError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: PlatformInvitationErrorCode,
  ) {
    super(message);
    this.name = "PlatformInvitationError";
  }
}

async function toPlatformInvitationError(
  res: Response,
  fallback: string,
): Promise<PlatformInvitationError> {
  const payload = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  return new PlatformInvitationError(
    res.status,
    payload.error ?? fallback,
    payload.code as PlatformInvitationErrorCode | undefined,
  );
}

// ---------------------------------------------------------------------------
// Funciones públicas
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/platform-invitation/preview — determina si la invitación es
 * aceptable. No requiere sesión, no incrementa ningún counter (read-only).
 */
export async function previewPlatformInvitation(
  invitationId: string,
): Promise<PlatformInvitationPreviewResponse> {
  const res = await platformInvitationPost("/api/auth/platform-invitation/preview", {
    invitationId,
  });
  if (!res.ok) {
    throw await toPlatformInvitationError(res, "Error al cargar la invitación");
  }
  return (await res.json()) as PlatformInvitationPreviewResponse;
}

/**
 * POST /api/auth/platform-invitation/accept-signup — crea la cuenta + la
 * membership de plataforma en una transacción. Éxito: cookie de sesión
 * seteada por el worker (viene en la respuesta, el browser la aplica sola).
 *
 * Errores posibles:
 *   - 403 → code/invitación inválida o expirada (mensaje genérico anti-oráculo)
 *   - 403 INVITATION_INVALIDATED → bloqueada por demasiados intentos
 *   - 409 USER_EXISTS → ya existe una cuenta con ese email
 *   - 422 → body inválido (name/password/acceptCode fuera de rango)
 *   - 429 → rate-limit por invitationId
 */
export async function acceptPlatformSignup(params: {
  invitationId: string;
  acceptCode: string;
  password: string;
  name: string;
}): Promise<PlatformAcceptSignupResponse> {
  const res = await platformInvitationPost("/api/auth/platform-invitation/accept-signup", {
    invitationId: params.invitationId,
    acceptCode: params.acceptCode,
    password: params.password,
    name: params.name,
  });
  if (!res.ok) {
    throw await toPlatformInvitationError(res, "Error al crear la cuenta");
  }
  return (await res.json()) as PlatformAcceptSignupResponse;
}
